import { useState } from "react";

export const useLocalStorage = (keyName, defaultValue) => {
  /**
   * Custom hook to manage local storage with state.
   * This hook initializes the state with a value from local storage,
   * and provides a function to update the value.
   * @param {string} keyName - The key under which the value is stored in local storage.
   * @param {any} defaultValue - The default value to use if the key does not exist in local storage.
   * @returns {[any, function]} An array containing the current value and a function to update it.
   * The function will update the value in local storage and state.
   * If the new value is null or undefined, it will remove the key from local storage.
   * @throws {Error} If there is an error accessing local storage.
   * This function will log the error to the console.
   */
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const value = window.localStorage.getItem(keyName);
      // Try to parse as JSON, return as-is if parsing fails
      if (value) {
        try {
          return JSON.parse(value);
        } catch {
          // If it's not valid JSON, return the raw value
          return value;
        }
      }
      return defaultValue;
    } catch (err) {
      console.error(err);
      return defaultValue;
    }
  });

  /**
   * Sets a new value in local storage and updates the state.
   * If the new value is null or undefined, it removes the key from local storage.
   * @param {any} newValue - The new value to set in local storage.
   * If null or undefined, the key will be removed from local storage.
   * @returns {void}
   * @throws {Error} If there is an error accessing local storage.
   * This function will log the error to the console.
   */
  const setValue = (newValue) => {
    // if null or undefined, remove the key from local storage
    if (newValue === null || newValue === undefined) {
      window.localStorage.removeItem(keyName);
      setStoredValue(defaultValue);
      return;
    }

    try {
      // Save to local storage
      const valueToStore =
        typeof newValue === "object" ? JSON.stringify(newValue) : newValue;

      window.localStorage.setItem(keyName, valueToStore);

      setStoredValue(newValue);
    } catch (err) {
      console.error(err);
    }
  };

  return [storedValue, setValue];
};
